const hoursNumber = 4;
const millisecondsPerMinute = 60 * 1000;
const millisecondsPerHour = 60 * millisecondsPerMinute;
const millisecondsPerDay = 24 * millisecondsPerHour;
const millisecondsPerMonth = 30 * millisecondsPerDay; // Not exact science, but we do not need it to be exact
const millisecondsPerYear = 12 * millisecondsPerMonth; // Not exact science, but we do not need it to be exact

const hoursInMs = hoursNumber * millisecondsPerHour;

export const months = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];

export const monthsShort = [
  'jan.',
  'feb.',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'aug.',
  'sep.',
  'okt.',
  'nov.',
  'des.',
];

export const weekdays = [
  'søndag',
  'mandag',
  'tirsdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lørdag',
];

/**
 * We use this function instead of moment when possible due to performance reasons.
 * This is aprox 22x faster than using moment for returning hours/minutes ago so especially for
 * things that are run on all articles on the frontpage moment should be avoided.
 * @param {*} date
 */
export function getTimeAgoString(date: Date): string {
  const millisecondsFromNow = Date.now() - date.getTime();
  const hours = Math.floor(millisecondsFromNow / millisecondsPerHour);
  if (hours === 1) {
    return `én time siden`;
  } else if (hours > 1) {
    return `${hours} timer siden`;
  } else {
    const minutes = Math.floor(millisecondsFromNow / millisecondsPerMinute);
    if (minutes === 1) {
      return `ett minutt siden`;
    } else if (minutes > 1) {
      return `${minutes} minutter siden`;
    } else {
      return 'noen sekunder siden';
    }
  }
}

/**
 * Formats date into norwegian string format. About 50x more efficient than moment
 * and about 10x more than date-fns
 * @param {*} date
 */
export function getDateTimeAgoString(date: Date): string {
  const millisecondsFromNow = Date.now() - date.getTime();
  const years = Math.floor(millisecondsFromNow / millisecondsPerYear);
  if (years === 1) {
    return `ett år siden`;
  } else if (years > 1) {
    return `${years} år siden`;
  } else {
    const months = Math.floor(millisecondsFromNow / millisecondsPerMonth);
    if (months === 1) {
      return `en måned siden`;
    } else if (months > 1) {
      return `${months} måneder siden`;
    } else {
      const days = Math.floor(millisecondsFromNow / millisecondsPerDay);
      if (days === 1) {
        return `en dag siden`;
      } else if (days > 1) {
        return `${days} dager siden`;
      } else {
        return getTimeAgoString(date);
      }
    }
  }
}

export function updatedTime(
  updated: string | number | Date,
): string | undefined {
  const d = new Date(updated);
  const fourHoursAgo = new Date(Date.now() - hoursInMs);

  if (d > fourHoursAgo) {
    return getTimeAgoString(d);
  }

  return undefined;
}

export function publishedTime(published: string | number | Date): string {
  const d = new Date(published);
  const hour = ('0' + d.getHours()).slice(-2);
  const minute = ('0' + d.getMinutes()).slice(-2);
  const date = d.getDate();
  const month = d.getMonth() + 1;

  return `${hour}:${minute} (${date}.${month})`;
}

export function timeSinceNowFormatted(date: string | number | Date): string {
  if (date) {
    return getDateTimeAgoString(new Date(date));
  }

  return '';
}

export function getTime(date: string | Date | undefined): string {
  if (date) {
    const d = new Date(date);
    const hour = ('0' + d.getHours()).slice(-2);
    const minute = ('0' + d.getMinutes()).slice(-2);

    return `${hour}:${minute}`;
  }

  return '';
}

export function todayLongDate(shortMonth?: boolean, year?: boolean): string {
  const today = new Date();

  return `${weekdays[today.getDay()]} ${today.getDate()}. ${
    shortMonth ? monthsShort[today.getMonth()] : months[today.getMonth()]
  } ${year ? today.getFullYear() : ''}`;
}

export function publishedDate(published: string | number | Date): string {
  if (published) {
    const d = new Date(published);
    const day = weekdays[d.getDay()];
    const date = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();

    return `${day} ${date} ${month} ${year}`;
  }

  return '';
}

export function isInTheFuture(date: string | number | Date): boolean {
  if (date) {
    const d = new Date(date);
    const today = new Date();

    return d > today;
  }

  return false;
}

export function getShortDate(
  date: Date | undefined,
  yearIncluded = true,
  delimiter = '.',
): string {
  if (!date) {
    return '';
  }

  let day = date.getDate().toString();
  let month = (date.getMonth() + 1).toString();

  day = ('0' + day).slice(-2);
  month = ('0' + month).slice(-2);

  if (yearIncluded) {
    return day + delimiter + month + delimiter + date.getFullYear();
  } else {
    return day + delimiter + month;
  }
}

export function getMonthName(date: string | Date, shortName?: boolean): string {
  if (date) {
    const d = new Date(date);

    return shortName ? monthsShort[d.getMonth()] : months[d.getMonth()];
  }

  return '';
}

export function getWeekdayName(date: Date): string {
  return weekdays[date.getDay()];
}
